import { useState, useEffect } from "react";
import { useTemplates } from "../../hooks/usePromptTemplates";
import { fetchTemplate } from "../../api/promptTemplates";
import type { TemplateCategory, TemplateSummary, TemplateRecord, TemplateVariable } from "../../api/promptTemplates";
import { Sheet } from "../../components/Sheet";

// ─── Pure helper ────────────────────────────────────────────────────────────────

/** Build the persona-update payload from a chosen template and its rendered prompt. */
export function buildApplyFields(
  template: Pick<TemplateRecord, "suggested_role" | "language">,
  renderedPrompt: string,
): { system_prompt_override: string; role: string; language: "en" | "es" | "hi" } {
  return {
    system_prompt_override: renderedPrompt,
    role: template.suggested_role,
    language: template.language,
  };
}

// ─── Props ──────────────────────────────────────────────────────────────────────

interface ApplyTemplateModalProps {
  open: boolean;
  personaName: string;
  onApply: (fields: { system_prompt_override: string; role: string; language: "en" | "es" | "hi" }) => void;
  onClose: () => void;
}

// ─── Category badge ─────────────────────────────────────────────────────────────

const CATEGORY_COLORS: Record<TemplateCategory, string> = {
  credential: "var(--accent)",
  pii: "var(--warning, #d97706)",
  access: "var(--info, #3b82f6)",
  financial: "var(--error)",
  custom: "var(--text-muted)",
};

const CATEGORY_LABELS: Record<TemplateCategory, string> = {
  credential: "Credential",
  pii: "PII",
  access: "Access",
  financial: "Financial",
  custom: "Custom",
};

function CategoryBadge({ category }: { category: TemplateCategory }) {
  const color = CATEGORY_COLORS[category] ?? "var(--text-muted)";
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 7px",
        borderRadius: 4,
        fontSize: 9,
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: 0.5,
        color,
        background: `color-mix(in srgb, ${color} 12%, transparent)`,
        border: `1px solid color-mix(in srgb, ${color} 25%, transparent)`,
      }}
    >
      {CATEGORY_LABELS[category] ?? category}
    </span>
  );
}

// ─── Auto-resolve mapping ───────────────────────────────────────────────────────

function autoResolveValue(
  autoResolve: string | null,
  personaName: string,
): string | null {
  if (!autoResolve) return null;
  if (autoResolve === "persona.display_name") return personaName;
  return null;
}

// ─── Component ───────────────────────────────────────────────────────────────────

export function ApplyTemplateModal({
  open,
  personaName,
  onApply,
  onClose,
}: ApplyTemplateModalProps) {
  const { data: listData, isLoading: listLoading } = useTemplates();

  // Step 1 = picker, Step 2 = variable form
  const [step, setStep] = useState<1 | 2>(1);
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateRecord | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [varValues, setVarValues] = useState<Record<string, string>>({});

  const [preview, setPreview] = useState("");

  // Reset state when modal opens/closes
  useEffect(() => {
    if (open) {
      setStep(1);
      setSelectedTemplate(null);
      setVarValues({});
      setPreview("");
    }
  }, [open]);

  // Client-side render — no API dependency
  useEffect(() => {
    if (step !== 2 || !selectedTemplate) return;
    let text = selectedTemplate.prompt_body;
    for (const v of selectedTemplate.variables) {
      if (varValues[v.key]) {
        text = text.replaceAll(`{${v.key}}`, varValues[v.key]);
      }
    }
    setPreview(text);
  }, [varValues, step, selectedTemplate]);

  async function handleSelectTemplate(summary: TemplateSummary) {
    setLoadingDetail(true);
    try {
      const resp = await fetchTemplate(summary.template_id);
      const rec = resp.record;
      setSelectedTemplate(rec);

      // Pre-fill auto-resolved variables
      const initial: Record<string, string> = {};
      for (const v of rec.variables) {
        const resolved = autoResolveValue(v.auto_resolve, personaName);
        if (resolved !== null) {
          initial[v.key] = resolved;
        }
      }
      setVarValues(initial);
      setStep(2);
    } catch {
      // Stay on step 1
    } finally {
      setLoadingDetail(false);
    }
  }

  function handleApply() {
    if (!selectedTemplate) return;
    const rendered = preview || selectedTemplate.prompt_body;
    onApply(buildApplyFields(selectedTemplate, rendered));
    onClose();
  }

  const items = listData?.items ?? [];

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={step === 1 ? "Choose a template" : `Configure: ${selectedTemplate?.name}`}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 16, fontFamily: "var(--font-sans)" }}>
        {/* Step 1: Template picker */}
        {step === 1 && (
          <>
            {listLoading ? (
              <div style={{ color: "var(--text-muted)", fontSize: 12, padding: 20, textAlign: "center" }}>
                Loading templates...
              </div>
            ) : items.length === 0 ? (
              <div style={{ color: "var(--text-muted)", fontSize: 12, padding: 20, textAlign: "center" }}>
                No templates available. Create one in the Template Library first.
              </div>
            ) : (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
                  gap: 10,
                }}
              >
                {items.map((t) => (
                  <button
                    key={t.template_id}
                    onClick={() => { void handleSelectTemplate(t); }}
                    disabled={loadingDetail}
                    style={{
                      background: "var(--bg-card)",
                      border: "1px solid var(--border-subtle)",
                      borderRadius: 10,
                      padding: "14px 16px",
                      textAlign: "left",
                      cursor: loadingDetail ? "wait" : "pointer",
                      display: "flex",
                      flexDirection: "column",
                      gap: 6,
                    }}
                  >
                    <CategoryBadge category={t.category} />
                    <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-primary)" }}>
                      {t.name}
                    </div>
                    <div
                      style={{
                        fontSize: 10.5,
                        color: "var(--text-muted)",
                        lineHeight: 1.4,
                        overflow: "hidden",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                      }}
                    >
                      {t.description}
                    </div>
                    <div style={{ fontSize: 9.5, color: "var(--text-muted)", marginTop: 2 }}>
                      {t.variable_count} variable{t.variable_count !== 1 ? "s" : ""} -- {t.suggested_role}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </>
        )}

        {/* Step 2: Variable form + preview */}
        {step === 2 && selectedTemplate && (
          <>
            {/* Variable inputs */}
            {selectedTemplate.variables.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <div style={{ fontSize: 11, color: "var(--text-secondary)", fontWeight: 600 }}>
                  Fill in template variables
                </div>
                {selectedTemplate.variables.map((v: TemplateVariable) => {
                  const resolved = autoResolveValue(v.auto_resolve, personaName);
                  const isAutoResolved = resolved !== null;
                  return (
                    <div key={v.key} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      <label
                        style={{
                          fontSize: 9.5,
                          textTransform: "uppercase",
                          letterSpacing: 0.6,
                          color: "var(--text-muted)",
                          fontWeight: 600,
                        }}
                      >
                        {v.label}
                        {isAutoResolved && (
                          <span style={{ marginLeft: 6, fontSize: 8.5, color: "var(--accent)", fontWeight: 500, textTransform: "none" }}>
                            auto-resolved
                          </span>
                        )}
                      </label>
                      <input
                        value={varValues[v.key] ?? ""}
                        onChange={(e) =>
                          setVarValues((prev) => ({ ...prev, [v.key]: e.target.value }))
                        }
                        readOnly={isAutoResolved}
                        placeholder={v.placeholder || `Enter ${v.label.toLowerCase()}`}
                        style={{
                          background: isAutoResolved ? "var(--bg-tertiary)" : "var(--bg-input)",
                          border: "1px solid var(--border)",
                          borderRadius: 8,
                          padding: "7px 10px",
                          color: "var(--text-primary)",
                          fontSize: 12,
                          fontFamily: "var(--font-sans)",
                          outline: "none",
                          opacity: isAutoResolved ? 0.8 : 1,
                          width: "100%",
                          boxSizing: "border-box",
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            )}

            {/* Live preview */}
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{ fontSize: 11, color: "var(--text-secondary)", fontWeight: 600 }}>
                Preview
              </div>
              <div
                style={{
                  background: "var(--bg-input)",
                  border: "1px solid var(--border)",
                  borderRadius: 8,
                  padding: "10px 12px",
                  fontFamily: "var(--font-mono)",
                  fontSize: 11,
                  color: "var(--text-primary)",
                  lineHeight: 1.6,
                  whiteSpace: "pre-wrap",
                  maxHeight: 200,
                  overflow: "auto",
                }}
              >
                {preview || selectedTemplate.prompt_body}
              </div>
            </div>

            {/* Suggested role display */}
            <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
              Suggested role: <strong style={{ color: "var(--text-primary)" }}>{selectedTemplate.suggested_role}</strong>
            </div>

            {/* Actions */}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button
                onClick={() => {
                  setStep(1);
                  setSelectedTemplate(null);
                  setVarValues({});
                  setPreview("");
                }}
                style={{
                  padding: "6px 14px",
                  borderRadius: 6,
                  border: "1px solid var(--border)",
                  background: "var(--bg-tertiary)",
                  color: "var(--text-secondary)",
                  fontSize: 11.5,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Back
              </button>
              <button
                onClick={handleApply}
                style={{
                  padding: "6px 14px",
                  borderRadius: 6,
                  border: "1px solid var(--accent)",
                  background: "var(--accent)",
                  color: "white",
                  fontSize: 11.5,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Apply template
              </button>
            </div>
          </>
        )}
      </div>
    </Sheet>
  );
}
